import React, { useState, useCallback } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { X, Ticket, CheckCircle, AlertCircle, ArrowRight, Loader2, Users, Moon, Star, Receipt } from 'lucide-react';
import { useWebDesignedCart } from '../../contexts/WebDesignedCartContext';
import type { VumPromo } from '../../contexts/WebDesignedCartContext';
import { addToCart, removeVumIncludedActivities, VUM_ACTIVITY_ITEM_PREFIX } from '../../services/cartService';
import { useLanguage } from '../../contexts/useLanguage';
import { quebecTaxFromPreTax, quebecStaySubtotalPricing, roundMoneyCAD, taxeHebergementAmountForNights } from '../../lib/quebecSalesTax';
import { resolveSpecificAccommodationIds } from '../../lib/vumAccommodationScope';

/** Forfait VUM: fixed TH per included night, TPS/TVQ on (forfait + TH). */
function computeVumTaxes(price: number, nights: number) {
  const th = taxeHebergementAmountForNights(nights, true);
  const q = quebecTaxFromPreTax(price + th);
  const totalTaxes = roundMoneyCAD(th + q.tps + q.tvq);
  return { gst: q.tps, qst: q.tvq, taxeHeberg: th, totalTaxes };
}

type QueryResult = ReturnType<typeof useQuery<typeof api.vumCodes.getByCodeForClient>>;

function useCodeLookup(code: string): QueryResult {
  return useQuery(api.vumCodes.getByCodeForClient, code.trim().length >= 3 ? { code } : 'skip');
}

export function VumPromoModal() {
  const { t } = useLanguage();
  const cartContext = useWebDesignedCart();
  const [inputCode, setInputCode] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [confirmedCode, setConfirmedCode] = useState('');

  const lookupResult = useCodeLookup(submitted ? confirmedCode : '');

  const handleCheck = useCallback(() => {
    if (!inputCode.trim()) return;
    setConfirmedCode(inputCode.trim().toUpperCase());
    setSubmitted(true);
  }, [inputCode]);

  const handleReset = useCallback(() => {
    setInputCode('');
    setSubmitted(false);
    setConfirmedCode('');
  }, []);

  const handleApply = useCallback(() => {
    if (!lookupResult || lookupResult === undefined || !('found' in lookupResult) || !lookupResult.found) return;
    const mergedSpecific = [
      ...((lookupResult as { specificAccommodationIds?: string[] }).specificAccommodationIds ?? []),
      ...((lookupResult as { specificAccommodationId?: string }).specificAccommodationId
        ? [(lookupResult as { specificAccommodationId?: string }).specificAccommodationId!]
        : []),
    ].map(String);
    const uniqueSpecific = Array.from(new Set(mergedSpecific));
    const promo: VumPromo = {
      vumCodeId: lookupResult.vumCodeId as string,
      code: lookupResult.code,
      guestFirstName: lookupResult.guestFirstName ?? '',
      guestLastName: lookupResult.guestLastName ?? '',
      packageName: lookupResult.packageName,
      optionLabel: lookupResult.optionLabel ?? '',
      nights: lookupResult.nights,
      forfaitPrice: lookupResult.price,
      maxPersons: lookupResult.maxPersons,
      minPersons: lookupResult.minPersons,
      maxAdults: lookupResult.maxAdults,
      maxChildren: lookupResult.maxChildren,
      accommodationTypes: lookupResult.accommodationTypes,
      specificAccommodationIds: uniqueSpecific.length > 0 ? uniqueSpecific : undefined,
      specificAccommodationId: uniqueSpecific.length === 1 ? uniqueSpecific[0] : undefined,
      includedActivities: lookupResult.includedActivities ?? [],
    };
    cartContext?.setVumPromo(promo);

    // Remove any VUM activity items from a previous promo, then add this promo's included activities
    removeVumIncludedActivities();
    promo.includedActivities.forEach((activity, index) => {
      if (!activity.label) return;
      const includedQty = activity.quantity ?? 1;
      addToCart({
        type: 'activity',
        itemId: `${VUM_ACTIVITY_ITEM_PREFIX}${promo.vumCodeId}-${index}`,
        itemName: activity.label,
        itemPrice: 0,
        quantity: includedQty,
        vumIncludedQty: includedQty,
        vumLinkedActivityPrice: (activity as any).activityPrice ?? undefined,
      });
    });

    cartContext?.setVumPromoModalOpen(false);
    handleReset();
  }, [lookupResult, cartContext, handleReset]);

  if (!cartContext?.vumPromoModalOpen) return null;

  const isLoading = submitted && lookupResult === undefined;
  const result = submitted && lookupResult !== undefined ? lookupResult : null;

  const errorMessage = (() => {
    if (!result || !('found' in result) || result.found) return null;
    switch (result.reason) {
      case 'not_found': return 'Code introuvable. Vérifiez l\'orthographe.';
      case 'cancelled': return 'Ce code a été annulé.';
      case 'already_used': return 'Ce code a déjà été utilisé pour une réservation.';
      case 'not_validated': return 'Ce code n\'a pas encore été validé par notre équipe. Si vous avez envoyé un courriel, veuillez patienter — vous recevrez une confirmation.';
      default: return 'Une erreur est survenue.';
    }
  })();

  const found = result && 'found' in result && result.found ? result : null;

  const accommodationLabel = (() => {
    if (!found) return '';
    const nSpecific = resolveSpecificAccommodationIds({
      specificAccommodationIds: (found as { specificAccommodationIds?: string[] }).specificAccommodationIds as
        | undefined,
      specificAccommodationId: (found as { specificAccommodationId?: string }).specificAccommodationId as
        | undefined,
    }).length;
    if (nSpecific > 0) {
      return nSpecific === 1 ? 'hébergement spécifique' : `${nSpecific} hébergements précis`;
    }
    if (found.forAllAccommodations) return 'tout type d\'hébergement';
    const types = found.accommodationTypes ?? [];
    if (types.length === 0) return 'tout type d\'hébergement';
    return types.map((t: string) => t.charAt(0).toUpperCase() + t.slice(1)).join(', ');
  })();

  return (
    <div className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/50 p-3 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex max-h-[min(92dvh,40rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between bg-gradient-to-r from-emerald-700 to-emerald-600 p-5">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-white/20 p-2">
              <Ticket className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Code promo VUM</h2>
              <p className="text-xs text-emerald-100">Entrez votre code promovalidation.com</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { cartContext?.setVumPromoModalOpen(false); handleReset(); }}
            className="rounded-lg p-1.5 text-white transition-colors hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-y-contain touch-pan-y p-6 pb-[max(1.25rem,env(safe-area-inset-bottom,0px))]">
          {/* Code input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Votre code promo</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={inputCode}
                onChange={(e) => { setInputCode(e.target.value.toUpperCase()); setSubmitted(false); setConfirmedCode(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
                placeholder="ex. ABC123"
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-emerald-500 uppercase"
              />
              <button
                type="button"
                onClick={handleCheck}
                disabled={inputCode.trim().length < 3 || isLoading}
                className="px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium text-sm flex items-center gap-1.5 disabled:opacity-50"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Vérifier'}
              </button>
            </div>
          </div>

          {/* Error */}
          {errorMessage && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}

          {/* Success — found code details */}
          {found && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-emerald-800 text-sm">Code valide !</p>
                  <p className="text-emerald-700 font-mono tracking-widest text-base mt-0.5">{found.code}</p>
                </div>
              </div>

              {/* Package details card */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
                  <p className="font-semibold text-gray-900 text-sm">{found.packageName}</p>
                  {found.optionLabel && <p className="text-xs text-gray-500 mt-0.5">{found.optionLabel}</p>}
                </div>
                <div className="p-4 grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <Moon className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Nuits incluses</p>
                      <p className="font-semibold text-gray-900 text-sm">{found.nights} nuit{found.nights > 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Capacité</p>
                      <p className="font-semibold text-gray-900 text-sm">
                        {found.minPersons ? `${found.minPersons}–` : ''}{found.maxPersons} pers.
                        {found.maxAdults ? ` (${found.maxAdults} ad.` : ''}
                        {found.maxChildren ? `, ${found.maxChildren} enf.)` : found.maxAdults ? ')' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 col-span-2">
                    <Star className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Hébergement</p>
                      <p className="font-semibold text-gray-900 text-sm capitalize">{accommodationLabel}</p>
                    </div>
                  </div>
                  {found.includedActivities && found.includedActivities.length > 0 && (
                    <div className="col-span-2 pt-2 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 mb-1.5">Activités incluses :</p>
                      <ul className="space-y-1">
                        {found.includedActivities.map((a: { label: string; quantity?: number; valueUpTo?: number }, i: number) => (
                          <li key={i} className="text-xs text-gray-700 flex items-start gap-1.5">
                            <span className="text-emerald-500 mt-0.5">•</span>
                            {a.quantity && a.quantity > 1 ? `${a.quantity}x ` : ''}{a.label}
                            {a.valueUpTo ? <span className="text-gray-400">(valeur jusqu'à {a.valueUpTo.toLocaleString('fr-CA')} $)</span> : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="px-4 py-3 bg-emerald-50 border-t border-emerald-100 flex items-center justify-between">
                  <span className="text-xs text-emerald-700 font-medium">Forfait payé via promovalidation.com</span>
                  <span className="font-bold text-emerald-800 text-lg">{found.price.toLocaleString('fr-CA')} $</span>
                </div>
              </div>

              {/* Tax breakdown on VUM forfait price */}
              {(() => {
                const taxes = computeVumTaxes(found.price, found.nights);
                return (
                  <div className="border border-blue-200 rounded-xl overflow-hidden">
                    <div className="bg-blue-50 px-4 py-2.5 border-b border-blue-100 flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-blue-600" />
                      <p className="text-sm font-semibold text-blue-800">Taxes applicables sur votre forfait</p>
                    </div>
                    <div className="p-4 space-y-2 text-sm">
                      <div className="flex justify-between text-gray-600">
                        <span>Base fiscale (forfait VUM)</span>
                        <span className="font-medium text-gray-800">{found.price.toFixed(2)} $</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>{t('common.taxLodgingLabel')}</span>
                        <span>{taxes.taxeHeberg.toFixed(2)} $</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>{t('common.taxGstLabel')}</span>
                        <span>{taxes.gst.toFixed(2)} $</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>{t('common.taxQstLabel')}</span>
                        <span>{taxes.qst.toFixed(2)} $</span>
                      </div>
                      <p className="text-xs text-gray-500 pt-0.5">{t('common.checkoutSalesTaxNote')}</p>
                      <div className="flex justify-between font-semibold text-blue-800 pt-2 border-t border-blue-100">
                        <span>Total taxes à payer</span>
                        <span>{taxes.totalTaxes.toFixed(2)} $</span>
                      </div>
                    </div>
                    <div className="px-4 py-2 bg-blue-50 border-t border-blue-100 text-xs text-blue-700">
                      Ces taxes s'appliquent au prix du forfait. Les nuits supplémentaires seront taxées séparément.
                    </div>
                  </div>
                );
              })()}

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                <strong>Note :</strong> Si vous réservez plus de {found.nights} nuit{found.nights > 1 ? 's' : ''}, les nuits supplémentaires seront facturées au tarif normal de l'hébergement choisi.
              </div>

              <button
                type="button"
                onClick={handleApply}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-semibold"
              >
                Choisir mon hébergement
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {!submitted && (
            <p className="text-xs text-gray-400 text-center">
              Vous avez reçu un code de notre équipe par courriel après validation sur promovalidation.com.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
